import type {Reducer} from 'redux';
import produce from 'immer';
import type {
  JOB_INFORMATION,
  JOB_LISTS,
  JOB_CUSTOM_FIELDS,
  JOB_LOCATION,
  MILESTONES,
  INTERVIEW_FLOW,
  JOB_STAGES,
  USER_BASE_INFO,
  JOB_HIRING_TEAM,
  STAGE_REMOVE_RESPONSE,
  CompanyInfoJdGen,
  ApprovalObject,
  EmployeeRole,
  JOB_SCORE_CARDS,
  SCORE_CARD_CONFIG_RESPONSE,
  FEEDBACK_CONFIG_RESPONSE,
  STEP_QUESTIONNAIRE_SECTIONS_RESPONSE,
  JOB_HIRING_STEPS,
  PENDING_APPROVALS,
  CandidateList,
  ApprovalStatus,
  Tag,
  HIERARCHY_LEVEL,
  SCORECARD,
  EMAIL_AUTOMATION_TEMPLATE,
  JOB_POSITION,
  CANDIDATE_JOB_MASKING,
  FILTERED_JOB_LIST,
  FILTERED_JOB_PAYLOAD,
  FILTERED_JOB_SELECTED,
  OFFERS_CANDIDATE_INFO,
  RESUME_PARSING,
  Candidate,
  CANDIDATE_FEEDBACK,
  INTERVIEW_SCHEDULE,
  HM_EXPERIENCE_SURVEY,
  ApplicantList,
  ApplicantListFilter,
  ApplicantListFilterMetadata,
  MicrosoftLoginResponse,
  CandidateApplication,
  SCORECARD_PARAMETER,
  AI_SUMMARY,
  EVALUATION,
  VOICE_PHONE_NUMBERS_AND_BOTS,
  PROFILE_ANALYSIS,
  PROFILE_ANALYSIS_FEEDBACK_VALUE,
} from 'src/types/crm';
import type {
  CrmJobActions,
  FetchProfileAnalysisAction,
  FetchProfileAnalysisSuccessAction,
  FetchProfileAnalysisErrorAction,
  SetProfileAnalysisFeedbackAction,
  SetProfileAnalysisFeedbackLoadingAction,
} from 'src/action-creators/crm';
import type {CrmTagActions} from 'src/action-creators/crm-new/tag';
import type {CrmScorecardActions} from 'src/action-creators/crm-new/scorecard';
import type {CrmEmailAutomationActions} from 'src/action-creators/crm-new/email-automation';
import type {CrmJobPositionActions} from 'src/action-creators/crm-new/position-mapping';
import type {RevertOptimisticMoveCandidateAction} from 'src/utils/crm';
import {GET_FILTERED_JOB, STORE_JOB_FILTERS} from 'src/action-creators/crm-new';

import {
  JOB_INFORMATION_INITIAL_STATE,
  UPDATE_JOB_INFORMATION,
  GET_JOB_LOCATIONS,
  GET_JOB_GRADES,
  GET_JOB_DEPARTMENTS,
  GET_CUSTOM_JOB_FIELDS,
  GET_JOB_HIRING_STAGES,
  GET_HIRING_MILESTONES,
  GET_JOB_INTERVIEW_FLOW,
  GET_JOB_INTERVIEW_FLOW_BY_CANDIDATE,
  GET_HIRING_TEAM,
  CLEAR_HIRING_TEAM,
  REMOVE_HIRING_STAGE,
  UPDATE_COMPANY_INFO_JD_GENERATE,
  UPDATE_APPROVALS,
  GET_EMPLOYEE_ROLE,
  GET_JOB_SCORE_CARDS,
  GET_JOB_STEPS,
  GET_CANDIDATE_JOB_STEPS,
  GET_SCORE_CARD_CONFIG,
  UPDATE_SCORE_CARD_CONFIG,
  GET_FEEDBACK_CONFIG,
  GET_STEP_QUESTIONNAIRE_SECTIONS,
  GET_APPROVAL_REQUEST_TIME,
  SELECTED_QUESTIONNAIRE_SECTION,
  GET_JOB_MATCHES,
  GET_USER_INFO,
  GET_PENDING_APPROVALS,
  UPDATE_APPROVAL_STATUS,
  GET_TAGS,
  EDIT_TAG,
  DELETE_TAG,
  GET_HIERARCHY_LEVELS,
  ADD_TAG,
  UPDATE_SELECTED_PENDING_OFFER_CANDIDATE,
  UPDATE_APPLICANT_INFORMATION,
  UPDATE_CANDIDATE_FEEDBACKS,
  UPDATE_CANDIDATE_INTERVIEW_SCHEDULES,
  UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS,
  UPDATE_COMPLETED_CANDIDATE_FEEDBACKS,
  UPDATE_COMPLETED_INTERVIEW_SCHEDULES,
  UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS,
  GET_INTERVIEWS_INFO,
  CRM_CANDIDATE_PARSING,
  GET_APPLICANT_LIST,
  GET_APPLICANT_LIST_OF_STAGE,
  GET_APPLICANT_LIST_FILTER_METADATA,
  GET_APPLICANT_DETAILS,
  GET_CANDIDATE_LIST,
  MICROSOFT_LOGIN_SUCCESS,
  UPDATE_INTERNAL_FORM_CONFIG,
  GET_UNTRIED_COUNT,
  OPTIMISTIC_MOVE_CANDIDATE_TO_STAGE,
  REVERT_OPTIMISTIC_MOVE_CANDIDATE,
  OPTIMISTIC_BULK_MOVE_CANDIDATES_TO_STAGE,
  REVERT_OPTIMISTIC_BULK_MOVE_CANDIDATES,
  ADD_CANDIDATE_TO_STAGE,
  UPDATE_OVERVIEW_DATA,
  UPDATE_VOICE_PHONE_NUMBERS_AND_BOTS,
  RESET_APPLICANT_LIST_OF_STAGE,
  UPDATE_APPLICANT_LIST_OF_STAGE_BY_ID,
  UPDATE_EVALUATION_FORM_DRAFT,
  FLUSH_EVALUATION_FORM_DRAFT,
  FETCH_PROFILE_ANALYSIS,
  FETCH_PROFILE_ANALYSIS_SUCCESS,
  FETCH_PROFILE_ANALYSIS_ERROR,
  SET_PROFILE_ANALYSIS_FEEDBACK,
  SET_PROFILE_ANALYSIS_FEEDBACK_LOADING,
} from 'src/utils/crm';

import {
  GET_ORG_SCORECARD,
  EDIT_SCORECARD_PARAMETER,
  ADD_SCORECARD_PARAMETER,
  DELETE_SCORECARD_PARAMETER,
  DELETE_SCORECARD_SECTION,
  ADD_SCORECARD_SECTION,
  EDIT_SCORECARD_SECTION,
} from 'src/action-creators/crm-new/scorecard';

import {
  GET_AUTOMATION_TEMPLATES,
  UPDATE_AUTOMATION_STATUS,
  GET_JOB_MASKING,
  UPDATE_JOB_MASKING,
} from 'src/action-creators/crm-new/email-automation';
import {
  GET_JOB_POSITIONS,
  ADD_JOB_POSITION,
  EDIT_JOB_POSITION,
} from 'src/action-creators/crm-new/position-mapping';


type State = {
  pending_approvals: PENDING_APPROVALS | null;
  user_info: USER_BASE_INFO | null;
  job_information: JOB_INFORMATION;
  grades: JOB_LISTS[] | null;
  locations: JOB_LOCATION[] | null;
  departments: JOB_LISTS[] | null;
  milestones: MILESTONES[] | null;
  jobCustomFields: JOB_CUSTOM_FIELDS[] | null;
  jobInterviewFlow: INTERVIEW_FLOW | null;
  jobInterviewFlowByCandidateId: Record<string, INTERVIEW_FLOW>;
  jobInterviewStages: INTERVIEW_FLOW | null;
  jobHiringStages: JOB_STAGES | null;
  jobHiringTeam: JOB_HIRING_TEAM | null;
  removeStage: STAGE_REMOVE_RESPONSE | null;
  jdGenCompanyInfo: CompanyInfoJdGen | null;
  approvals: Array<ApprovalObject> | null;
  employeeRoles: EmployeeRole[] | null;
  jobScoreCards: JOB_SCORE_CARDS | null;
  jobScorecardConfigData: SCORE_CARD_CONFIG_RESPONSE[] | null;
  feedbackConfiguration: FEEDBACK_CONFIG_RESPONSE | null;
  stepQuestionnaireSection: STEP_QUESTIONNAIRE_SECTIONS_RESPONSE[] | null;
  approvalRequestTime: string | null;
  jobSteps: JOB_HIRING_STEPS[] | null;
  selectedQuestionnaireSection: JOB_STAGES | null;
  jobMatches: CandidateList | null;
  approvalStatus: ApprovalStatus | null;
  tags: Tag[] | null;
  hierarchyLevels: HIERARCHY_LEVEL[] | null;
  scorecard: SCORECARD | null;
  emailAutomationTemplates: EMAIL_AUTOMATION_TEMPLATE[] | null;
  jobPositions: JOB_POSITION[] | null;
  jobMasking: CANDIDATE_JOB_MASKING | null;
  filteredJobList: FILTERED_JOB_LIST | null;
  selectedPendingOfferCandidate: OFFERS_CANDIDATE_INFO | null;
  applicantInformation: Candidate | null;
  candidateJobSteps: JOB_HIRING_STEPS[] | null;
  candidateParsing: RESUME_PARSING | null;
  candidateFeedbacks: CANDIDATE_FEEDBACK[] | null;
  candidateInterviewSchedules: INTERVIEW_SCHEDULE[] | null;
  candidateHmExperienceSurveys: HM_EXPERIENCE_SURVEY[] | null;
  completedCandidateFeedbacks: CANDIDATE_FEEDBACK[] | null;
  completedInterviewSchedules: INTERVIEW_SCHEDULE[] | null;
  completedHmExperienceSurveys: HM_EXPERIENCE_SURVEY[] | null;
  applicantList: ApplicantList | null;
  applicantListByStage: Record<number, ApplicantList> | null;
  applicantListFilterMetadata: ApplicantListFilterMetadata | null;
  applicantDetails: Candidate[] | null;
  candidateList: CandidateList | null;
  microsoftLoginResponse: MicrosoftLoginResponse | null;
  untriedCount: number | null;
  storedJobFilters: {
    selectedFilters: FILTERED_JOB_PAYLOAD | null;
    rawFilteredSelected: FILTERED_JOB_SELECTED | null;
    selectedJobStatuses: string[] | null;
    searchedText: string;
    isMyJobSelected: boolean;
  };
  overviewData: {
    aiSummary: AI_SUMMARY;
    evaluation: EVALUATION;
    voiceCallInfo: null;
  };
  voicePhoneNumbersAndBots: VOICE_PHONE_NUMBERS_AND_BOTS;
  profileAnalysis: {
    dataByCandidateId: {[candidateId: string]: PROFILE_ANALYSIS};
    feedbackByCandidateId: {
      [candidateId: string]: PROFILE_ANALYSIS_FEEDBACK_VALUE;
    };
    loadingByCandidateId: {[candidateId: string]: boolean};
    feedbackLoadingByCandidateId: {[candidateId: string]: boolean};
    errorByCandidateId: {[candidateId: string]: string | null};
  };
};

const initialState = {
  user_info: null,
  pending_approvals: null,
  job_information: JOB_INFORMATION_INITIAL_STATE,
  applicantInformation: null,
  grades: null,
  locations: null,
  departments: null,
  milestones: null,
  jobInterviewFlow: null,
  jobInterviewFlowByCandidateId: {},
  jobCustomFields: null,
  jobHiringTeam: null,
  jobInterviewStages: null,
  jobHiringStages: null,
  removeStage: null,
  jdGenCompanyInfo: null,
  approvals: null,
  employeeRoles: null,
  jobScoreCards: null,
  jobScorecardConfigData: null,
  feedbackConfiguration: null,
  stepQuestionnaireSection: null,
  approvalRequestTime: null,
  jobSteps: null,
  selectedQuestionnaireSection: null,
  jobMatches: null,
  approvalStatus: null,
  tags: null,
  hierarchyLevels: null,
  scorecard: null,
  emailAutomationTemplates: null,
  positionMapping: null,
  jobMasking: null,
  filteredJobList: null,
  selectedPendingOfferCandidate: null,
  candidateJobSteps: null,
  candidateParsing: null,
  candidateFeedbacks: null,
  candidateInterviewSchedules: null,
  candidateHmExperienceSurveys: null,
  completedCandidateFeedbacks: null,
  completedInterviewSchedules: null,
  completedHmExperienceSurveys: null,
  applicantList: null,
  applicantListByStage: null,
  applicantListFilterMetadata: null,
  applicantDetails: null,
  candidateList: null,
  microsoftLoginResponse: null,
  untriedCount: null,
  storedJobFilters: {
    selectedFilters: null,
    rawFilteredSelected: null,
    selectedJobStatuses: null,
    searchedText: '',
    isMyJobSelected: true,
  },
  overviewData: {
    aiSummary: null,
    evaluation: null,
    voiceCallInfo: null,
  },
  voicePhoneNumbersAndBots: null,
  evaluationFormDrafts: {},
  profileAnalysis: {
    dataByCandidateId: {},
    feedbackByCandidateId: {},
    loadingByCandidateId: {},
    feedbackLoadingByCandidateId: {},
    errorByCandidateId: {},
  },
} as const;

// @ts-expect-error - TS2322 - Type '(state: State | null | undefined, action: CrmJobActions | CrmTagActions | CrmScorecardActions | CrmEmailAutomationActions | CrmJobPositionActions) => { ...; } | ... 44 more ... | null' is not assignable to type 'Reducer<State, CrmJobActions | CrmTagActions | CrmScorecardActions | CrmJobPositionActions>'.
const reducer: Reducer<
  State,
  CrmJobActions | CrmTagActions | CrmScorecardActions | CrmJobPositionActions
> = (
  // @ts-expect-error - TS2741 - Property 'jobPositions' is missing in type '{ readonly user_info: null; readonly pending_approvals: null; readonly job_information: { readonly id: 0; readonly createdBy: 0; readonly createdOn: 0; readonly updatedBy: 0; readonly updatedOn: 0; ... 65 more ...; readonly workplaceType: null; }; ... 42 more ...; readonly microsoftLoginResponse: null; }' but required in type 'State'.
  state: State = initialState,
  action:
    | CrmJobActions
    | CrmTagActions
    | CrmScorecardActions
    | CrmEmailAutomationActions
    | CrmJobPositionActions
    | FetchProfileAnalysisAction
    | FetchProfileAnalysisSuccessAction
    | FetchProfileAnalysisErrorAction
    | SetProfileAnalysisFeedbackAction
    | SetProfileAnalysisFeedbackLoadingAction,
) => {
  switch (action.type) {
    case UPDATE_VOICE_PHONE_NUMBERS_AND_BOTS:
      return {
        ...state,
        voicePhoneNumbersAndBots: action.payload,
      };
    case UPDATE_OVERVIEW_DATA:
      const {aiSummary, evaluation, voiceCallInfo} = action.payload;
      return {
        ...state,
        overviewData: {
          ...state.overviewData,
          aiSummary,
          evaluation,
          voiceCallInfo,
        },
      };
    case UPDATE_JOB_INFORMATION:
      return {
        ...state,
        job_information: {
          ...action.payload,
        },
      };
    case GET_JOB_LOCATIONS:
      return {
        ...state,
        locations: action.payload,
      };
    case GET_JOB_GRADES:
      return {
        ...state,
        grades: action.payload,
      };
    case GET_JOB_DEPARTMENTS:
      return {
        ...state,
        departments: action.payload,
      };
    case GET_HIRING_MILESTONES:
      return {
        ...state,
        milestones: action.payload,
      };
    case GET_CUSTOM_JOB_FIELDS:
      return {
        ...state,
        jobCustomFields: action.payload,
      };
    case GET_JOB_INTERVIEW_FLOW:
      return {
        ...state,
        jobInterviewFlow: action.payload,
      };
    case GET_JOB_INTERVIEW_FLOW_BY_CANDIDATE: {
      const {candidateId, interviewFlow} = action.payload;
      return {
        ...state,
        jobInterviewFlowByCandidateId: {
          ...state.jobInterviewFlowByCandidateId,
          [candidateId]: interviewFlow,
        },
      };
    }
    case GET_JOB_HIRING_STAGES:
      return {
        ...state,
        jobInterviewFlow: {...state.jobInterviewFlow, stages: action.payload},
      };
    case GET_HIRING_TEAM:
      return {
        ...state,
        jobHiringTeam: action.payload,
      };
    case CLEAR_HIRING_TEAM:
      return {
        ...state,
        jobHiringTeam: null,
      };
    case REMOVE_HIRING_STAGE:
      return {
        ...state,
        removeStage: action.payload,
      };
    case UPDATE_COMPANY_INFO_JD_GENERATE:
      return {
        ...state,
        jdGenCompanyInfo: action.payload,
      };
    case UPDATE_APPROVALS:
      return {
        ...state,
        approvals: action.payload,
      };
    case GET_EMPLOYEE_ROLE:
      return {
        ...state,
        employeeRoles: action.payload,
      };
    case GET_JOB_STEPS:
      return {
        ...state,
        jobSteps: action.payload,
      };
    case GET_CANDIDATE_JOB_STEPS:
      return {
        ...state,
        candidateJobSteps: action.payload,
      };
    case GET_JOB_SCORE_CARDS:
      return {
        ...state,
        jobScoreCards: action.payload,
      };
    case GET_SCORE_CARD_CONFIG:
      return {
        ...state,
        jobScorecardConfigData: action.payload,
      };
    case UPDATE_SCORE_CARD_CONFIG:
      return {
        ...state,
        jobScorecardConfigData: state.jobScorecardConfigData?.map(
          (section: SCORE_CARD_CONFIG_RESPONSE) => {
            return section.id === action.payload.id ? action.payload : section;
          },
        ),
      };
    case GET_FEEDBACK_CONFIG:
      return {
        ...state,
        feedbackConfiguration: action.payload,
      };
    case GET_STEP_QUESTIONNAIRE_SECTIONS:
      return {
        ...state,
        stepQuestionnaireSection: action.payload,
      };
    case GET_APPROVAL_REQUEST_TIME:
      return {
        ...state,
        approvalRequestTime: action.payload,
      };

    case SELECTED_QUESTIONNAIRE_SECTION:
      return {
        ...state,
        selectedQuestionnaireSection: action.payload,
      };
    case GET_JOB_MATCHES:
      return {
        ...state,
        jobMatches: action.payload,
      };
    case GET_USER_INFO:
      return {
        ...state,
        user_info: action.payload,
      };
    case GET_PENDING_APPROVALS:
      return {
        ...state,
        pending_approvals: action.payload,
      };
    case UPDATE_APPROVAL_STATUS:
      return {
        ...state,
        approvalStatus: action.payload,
      };
    case GET_TAGS:
      return {
        ...state,
        tags: action.payload,
      };
    case EDIT_TAG:
      const newTags = (state.tags || []).map((tag) => {
        if (tag.id === action.payload.id) {
          return {
            ...action.payload,
          };
        }
        return tag;
      });
      return {
        ...state,
        tags: newTags,
      };
    // @ts-expect-error - TS2678 - Type '"add-tag"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case ADD_TAG:
      return {
        ...state,
        // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS18047 - 'state' is possibly 'null'. | TS2339 - Property 'payload' does not exist on type 'never'. | TS2339 - Property 'payload' does not exist on type 'never'.
        tags: state.tags ? [...state.tags, action.payload] : [action.payload],
      };
    case DELETE_TAG:
      const updatedTags = state.tags
        ? // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2367 - This comparison appears to be unintentional because the types 'number' and 'Tag' have no overlap.
          state.tags.filter((tag) => tag.id !== action.payload)
        : [];
      return {
        ...state,
        tags: updatedTags,
      };
    case GET_HIERARCHY_LEVELS:
      return {
        ...state,
        hierarchyLevels: action.payload,
      };
    case GET_ORG_SCORECARD:
      return {
        ...state,
        scorecard: action.payload,
      };
    case EDIT_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          const parameter = section.parameters?.find(
            (parameter: SCORECARD_PARAMETER) =>
              parameter.id === action.payload.id,
          );

          if (parameter) {
            Object.assign(parameter, action.payload);
          }
        }
      });

    case ADD_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          section.parameters?.push(action.payload);
        }
      });
    case DELETE_SCORECARD_PARAMETER:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.sectionId,
          );

        if (section) {
          section.parameters = section.parameters?.filter(
            (parameter: SCORECARD_PARAMETER) =>
              parameter.id !== action.payload.id,
          );
        }
      });

    case DELETE_SCORECARD_SECTION:
      return produce(state, (draft) => {
        const scorecard = draft.scorecard;
        if (scorecard !== null && scorecard.sections) {
          scorecard.sections = scorecard.sections.filter(
            (section) => section.id !== action.payload.id,
          );
        }
      });
    case ADD_SCORECARD_SECTION:
      return produce(state, (draft) => {
        if (draft.scorecard) {
          draft.scorecard.sections = [
            ...(draft.scorecard.sections || []),
            ...action.payload,
          ];
        }
      });

    case EDIT_SCORECARD_SECTION:
      return produce(state, (draft) => {
        const section =
          draft.scorecard &&
          draft.scorecard.sections &&
          draft.scorecard.sections.find(
            (section) => section.id === action.payload.id,
          );

        if (section) {
          Object.assign(section, action.payload);
        }
      });
    case GET_AUTOMATION_TEMPLATES:
      return {
        ...state,
        emailAutomationTemplates: action.payload,
      };
    case UPDATE_AUTOMATION_STATUS:
      return {
        ...state,
        emailAutomationTemplates: (state.emailAutomationTemplates || []).map(
          (template) => {
            if (template.id === action.payload.id) {
              return {
                ...template,
                status: action.payload.status,
              };
            }
            return template;
          },
        ),
      };
    case GET_JOB_POSITIONS:
      return {
        ...state,
        jobPositions: action.payload,
      };
    case EDIT_JOB_POSITION:
      return produce(state, (draft) => {
        const position = draft.jobPositions?.find(
          (pos: JOB_POSITION) => pos.id === action.payload.id,
        );

        if (position) {
          Object.assign(position, action.payload);
        }
      });
    // @ts-expect-error - TS2678 - Type '"add-job-position"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case ADD_JOB_POSITION:
      return {
        ...state,
        jobPositions: state.jobPositions
          ? // @ts-expect-error - TS18047 - 'state' is possibly 'null'. | TS2339 - Property 'payload' does not exist on type 'never'.
            [...state.jobPositions, action.payload]
          : // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
            [action.payload],
      };
    // @ts-expect-error - TS2678 - Type '"get-job-masking"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_JOB_MASKING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobMasking: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-job-masking"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_JOB_MASKING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        jobMasking: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"get-filtered-job"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case GET_FILTERED_JOB:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        filteredJobList: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"store-job-filters"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case STORE_JOB_FILTERS:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        storedJobFilters: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-selected-candidate"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_SELECTED_PENDING_OFFER_CANDIDATE:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        selectedPendingOfferCandidate: action.payload,
      };
    case UPDATE_APPLICANT_INFORMATION:
      return {
        ...state,
        applicantDetails: (() => {
          const details = state.applicantDetails || [];
          const index = details.findIndex(
            (existing) => existing.id === action.payload.id,
          );
          if (index === -1) {
            return [...details, action.payload];
          } else {
            const updated = [...details];
            updated[index] = action.payload;
            return updated;
          }
        })(),
        applicantInformation: {
          ...action.payload,
        },
      };
    case UPDATE_CANDIDATE_FEEDBACKS:
      return {
        ...state,
        candidateFeedbacks: action.payload,
      };
    case UPDATE_CANDIDATE_INTERVIEW_SCHEDULES:
      return {
        ...state,
        candidateInterviewSchedules: action.payload,
      };
    case UPDATE_CANDIDATE_HM_EXPERIENCE_SURVEYS:
      return {
        ...state,
        candidateHmExperienceSurveys: action.payload,
      };
    case UPDATE_COMPLETED_CANDIDATE_FEEDBACKS:
      return {
        ...state,
        completedCandidateFeedbacks: action.payload,
      };
    case UPDATE_COMPLETED_INTERVIEW_SCHEDULES:
      return {
        ...state,
        completedInterviewSchedules: action.payload,
      };
    case UPDATE_COMPLETED_HM_EXPERIENCE_SURVEYS:
      return {
        ...state,
        completedHmExperienceSurveys: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"crm-candidate-parsing"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case CRM_CANDIDATE_PARSING:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        candidateParsing: action.payload,
      };
    case GET_APPLICANT_LIST:
      return {
        ...state,
        applicantList: action.payload,
      };
    case RESET_APPLICANT_LIST_OF_STAGE:
      return {
        ...state,
        applicantListByStage: {},
      };
    case UPDATE_APPLICANT_LIST_OF_STAGE_BY_ID: {
      const {applicantListByStage = {}} = state;
      const stageId = action.stageId;
      const updatedApplicantListByStage = {...applicantListByStage};
      updatedApplicantListByStage[stageId] = action.payload;
      return {
        ...state,
        applicantListByStage: updatedApplicantListByStage,
      };
    }
    case GET_APPLICANT_LIST_OF_STAGE: {
      const existingList = state.applicantListByStage?.[action.stageId];
      const staleCandidates =
        existingList?.content?.filter((c) => c.stale) || [];

      if (existingList?.content) {
        // Create a Set of candidate IDs from payload to deduplicate
        const payloadCandidateIds = new Set(
          action.payload.content.map((c) => c.id),
        );
        const staleCandidateIds = new Set(staleCandidates.map((c) => c.id));
        const payloadContainsStaleCandidates = action.payload.content.some(
          (c) => staleCandidateIds.has(c.id),
        );
        let mergedContent: typeof existingList.content;
        const nonStaleExistingContent = existingList.content.filter(
          (c) => !c.stale,
        );
        if (payloadContainsStaleCandidates) {
          // Replace stale candidates: remove stale ones from existing, then merge with payload
          // Filter out existing candidates that are in payload to avoid duplicates
          const filteredNonStaleContent = nonStaleExistingContent.filter(
            (c) => !payloadCandidateIds.has(c.id),
          );
          mergedContent = [
            ...filteredNonStaleContent,
            ...action.payload.content,
          ];
        } else {
          // Filter out existing candidates (both stale and non-stale) that are in payload to avoid duplicates
          const filteredStaleCandidates = staleCandidates.filter(
            (c) => !payloadCandidateIds.has(c.id),
          );
          const filteredNonStaleContent = nonStaleExistingContent.filter(
            (c) => !payloadCandidateIds.has(c.id),
          );
          mergedContent = [
            ...filteredStaleCandidates,
            ...filteredNonStaleContent,
            ...action.payload.content,
          ];
        }
        return {
          ...state,
          applicantListByStage: {
            ...(state.applicantListByStage || {}),
            [action.stageId]: {
              ...action.payload,
              content: mergedContent,
              numberOfElements: mergedContent.length,
              totalElements: action.payload.totalElements,
            },
          },
        };
      }
      // Replace mode: replace entire list
      return {
        ...state,
        applicantListByStage: {
          ...(state.applicantListByStage || {}),
          [action.stageId]: {
            ...action.payload,
            content: action.payload.content,
            numberOfElements: action.payload.numberOfElements,
            totalElements: action.payload.totalElements,
          },
        },
      };
    }
    case OPTIMISTIC_MOVE_CANDIDATE_TO_STAGE: {
      const {
        candidateId,
        sourceStageId,
        destinationStageId,
        candidate,
        collapsedDestinationStageListCount,
      } = action.payload;
      const applicantListByStage = {...(state.applicantListByStage || {})};
      const sourceStageList = applicantListByStage[sourceStageId];
      const destinationStageList = applicantListByStage[destinationStageId];
      const destinationStageListNumberOfElements =
        destinationStageList?.numberOfElements ||
        collapsedDestinationStageListCount ||
        0;
      const destinationStageListTotalElements =
        destinationStageList?.totalElements ||
        collapsedDestinationStageListCount ||
        0;

      // Remove candidate from source stage
      if (sourceStageList?.content) {
        const updatedSourceContent = sourceStageList.content.filter(
          (c) => c.id !== candidateId,
        );
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: updatedSourceContent,
          numberOfElements: Math.max(0, sourceStageList.numberOfElements - 1),
          totalElements: Math.max(0, sourceStageList.totalElements - 1),
        };
      }

      // Add candidate to destination stage
      if (destinationStageList?.content) {
        const updatedDestinationContent = [
          {...candidate, stale: true},
          ...destinationStageList.content,
        ];
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: updatedDestinationContent,
          numberOfElements: destinationStageList.numberOfElements + 1,
          totalElements: destinationStageList.totalElements + 1,
        };
      } else if (destinationStageList) {
        // If destination stage exists but has 0 candidates, initialize it
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: [{...candidate, stale: true}],
          numberOfElements: 1,
          totalElements: (destinationStageList.totalElements || 0) + 1,
        };
      } else {
        // If destination stage does not exist at all, create it and add the candidate optimistically
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        applicantListByStage[destinationStageId] = {
          content: [{...candidate, stale: true}],
          numberOfElements: destinationStageListNumberOfElements + 1,
          totalElements: destinationStageListTotalElements + 1,
        };
      }

      return {
        ...state,
        applicantListByStage,
      };
    }
    case REVERT_OPTIMISTIC_MOVE_CANDIDATE: {
      const {
        candidateId,
        sourceStageId,
        destinationStageId,
        candidate,
        originalIndex,
      } = (action as RevertOptimisticMoveCandidateAction).payload;
      const applicantListByStage = {...(state.applicantListByStage || {})};
      const sourceStageList = applicantListByStage[sourceStageId];
      const destinationStageList = applicantListByStage[destinationStageId];

      // Remove candidate from destination stage (revert)
      if (destinationStageList?.content) {
        const updatedDestinationContent = destinationStageList.content.filter(
          (candidate) => candidate.id !== candidateId,
        );
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: updatedDestinationContent,
          numberOfElements: Math.max(
            0,
            destinationStageList.numberOfElements - 1,
          ),
          totalElements: Math.max(0, destinationStageList.totalElements - 1),
        };
      }

      // Add candidate back to source stage at original position (revert)
      if (sourceStageList?.content) {
        // Restore candidate to its original position using originalIndex
        const updatedSourceContent = [
          ...sourceStageList.content.slice(0, originalIndex),
          candidate,
          ...sourceStageList.content.slice(originalIndex),
        ];
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: updatedSourceContent,
          numberOfElements: sourceStageList.numberOfElements + 1,
          totalElements: sourceStageList.totalElements + 1,
        };
      } else if (sourceStageList) {
        // If source stage exists but has no content array, initialize it
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: [candidate],
          numberOfElements: 1,
          totalElements: (sourceStageList.totalElements || 0) + 1,
        };
      }

      return {
        ...state,
        applicantListByStage,
      };
    }
    case OPTIMISTIC_BULK_MOVE_CANDIDATES_TO_STAGE: {
      const {
        candidates,
        sourceStageId,
        destinationStageId,
        collapsedDestinationStageListCount,
      } = action.payload;
      if (candidates.length === 0) {
        return state;
      }
      const applicantListByStage = {...(state.applicantListByStage || {})};
      const sourceStageList = applicantListByStage[sourceStageId];
      const destinationStageList = applicantListByStage[destinationStageId];
      const destinationStageListNumberOfElements =
        destinationStageList?.numberOfElements ||
        collapsedDestinationStageListCount ||
        0;
      const destinationStageListTotalElements =
        destinationStageList?.totalElements ||
        collapsedDestinationStageListCount ||
        0;
      const candidateIdsToRemove = new Set(
        candidates.map((c) => c.candidateId),
      );

      // Remove all candidates from source stage in one pass
      if (sourceStageList?.content) {
        const updatedSourceContent = sourceStageList.content.filter(
          (c) => !candidateIdsToRemove.has(c.id),
        );
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: updatedSourceContent,
          numberOfElements: Math.max(
            0,
            sourceStageList.numberOfElements - candidates.length,
          ),
          totalElements: Math.max(
            0,
            sourceStageList.totalElements - candidates.length,
          ),
        };
      }

      // Prepend all candidates to destination (reverse order so first-selected appears first)
      const candidatesToPrepend = [...candidates]
        .reverse()
        .map(({candidate}) => ({...candidate, stale: true}));
      if (destinationStageList?.content) {
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: [...candidatesToPrepend, ...destinationStageList.content],
          numberOfElements:
            destinationStageList.numberOfElements + candidates.length,
          totalElements: destinationStageList.totalElements + candidates.length,
        };
      } else if (destinationStageList) {
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: candidatesToPrepend,
          numberOfElements: candidates.length,
          totalElements:
            (destinationStageList.totalElements || 0) + candidates.length,
        };
      } else {
        // If destination stage does not exist at all, create it and add candidates optimistically
        // @ts-expect-error - TS2339 - Partial ApplicantList for optimistic bulk move; full type has empty, first, last, etc.
        applicantListByStage[destinationStageId] = {
          content: candidatesToPrepend,
          numberOfElements:
            destinationStageListNumberOfElements + candidates.length,
          totalElements: destinationStageListTotalElements + candidates.length,
        };
      }

      return {
        ...state,
        applicantListByStage,
      };
    }
    case REVERT_OPTIMISTIC_BULK_MOVE_CANDIDATES: {
      const {candidates, sourceStageId, destinationStageId} = action.payload;
      if (candidates.length === 0) {
        return state;
      }
      const applicantListByStage = {...(state.applicantListByStage || {})};
      const sourceStageList = applicantListByStage[sourceStageId];
      const destinationStageList = applicantListByStage[destinationStageId];
      const revertedCandidateIds = new Set(
        candidates.map((c) => c.candidateId),
      );

      // Remove all reverted candidates from destination stage
      if (destinationStageList?.content) {
        const updatedDestinationContent = destinationStageList.content.filter(
          (c) => !revertedCandidateIds.has(c.id),
        );
        applicantListByStage[destinationStageId] = {
          ...destinationStageList,
          content: updatedDestinationContent,
          numberOfElements: Math.max(
            0,
            destinationStageList.numberOfElements - candidates.length,
          ),
          totalElements: Math.max(
            0,
            destinationStageList.totalElements - candidates.length,
          ),
        };
      }

      // Restore each candidate to source stage at originalIndex (sorted ascending)
      const sortedByIndex = [...candidates].sort(
        (a, b) => a.originalIndex - b.originalIndex,
      );
      if (sourceStageList?.content) {
        let updatedSourceContent = [...sourceStageList.content];
        for (const {candidate, originalIndex} of sortedByIndex) {
          updatedSourceContent = [
            ...updatedSourceContent.slice(0, originalIndex),
            candidate,
            ...updatedSourceContent.slice(originalIndex),
          ];
        }
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: updatedSourceContent,
          numberOfElements:
            sourceStageList.numberOfElements + candidates.length,
          totalElements: sourceStageList.totalElements + candidates.length,
        };
      } else if (sourceStageList) {
        applicantListByStage[sourceStageId] = {
          ...sourceStageList,
          content: sortedByIndex.map(({candidate}) => candidate),
          numberOfElements: candidates.length,
          totalElements:
            (sourceStageList.totalElements || 0) + candidates.length,
        };
      }

      return {
        ...state,
        applicantListByStage,
      };
    }
    case ADD_CANDIDATE_TO_STAGE: {
      const DEFAULT_PAGE_SIZE = 10;
      const {stageId, candidate} = action;
      const applicantListByStage = {...(state.applicantListByStage || {})};

      // Remove candidate from ALL other stages first to prevent duplicates
      // This handles edge cases where multiple fetches complete after rapid drag-and-drop
      // todo if there are like 400 or big number of candidates that it will be a problem as it will freeze the UI
      for (const [otherStageIdStr, otherStageList] of Object.entries(
        applicantListByStage,
      )) {
        const otherStageId = parseInt(otherStageIdStr, 10);
        if (
          otherStageId !== stageId &&
          otherStageList?.content?.some((c) => c.id === candidate.id)
        ) {
          applicantListByStage[otherStageId] = {
            ...otherStageList,
            content: otherStageList.content.filter(
              (c) => c.id !== candidate.id,
            ),
            numberOfElements: Math.max(0, otherStageList.numberOfElements - 1),
            totalElements: Math.max(0, (otherStageList.totalElements || 1) - 1),
          };
        }
      }

      const stageList = applicantListByStage[stageId];

      // Check if candidate already exists in the stage list to avoid duplicates
      if (stageList?.content) {
        const candidateExists = stageList.content.some(
          (c) => c.id === candidate.id,
        );
        if (candidateExists) {
          // Candidate already exists, update in place with fresh data
          const myList = stageList.content.filter((c) => c.id !== candidate.id);
          return {
            ...state,
            applicantListByStage: {
              ...applicantListByStage,
              [stageId]: {
                ...stageList,
                content: [{...candidate}, ...myList],
                numberOfElements:
                  stageList.numberOfElements < DEFAULT_PAGE_SIZE
                    ? myList.length + 1
                    : stageList.numberOfElements,
                totalElements:
                  stageList.totalElements < DEFAULT_PAGE_SIZE
                    ? myList.length + 1
                    : stageList.totalElements,
              },
            },
          };
        }

        // Add candidate to the stage list
        applicantListByStage[stageId] = {
          ...stageList,
          content: [candidate, ...stageList.content],
          numberOfElements: stageList.numberOfElements + 1,
          totalElements: (stageList.totalElements || 0) + 1,
        };
      } else if (stageList) {
        // If stage exists but has no content array, initialize it
        applicantListByStage[stageId] = {
          ...stageList,
          content: [candidate],
          numberOfElements: 1,
          totalElements: (stageList.totalElements || 0) + 1,
        };
      } else {
        // If stage doesn't exist, create a new list with the candidate
        applicantListByStage[stageId] = {
          empty: false,
          first: true,
          last: true,
          number: 0,
          numberOfElements: 1,
          pageable: {},
          size: 1,
          sort: {},
          totalElements: 1,
          totalPages: 1,
          content: [candidate],
        };
      }

      return {
        ...state,
        applicantListByStage,
      };
    }
    case GET_APPLICANT_LIST_FILTER_METADATA:
      return {
        ...state,
        applicantListFilterMetadata: action.payload,
      };
    case GET_APPLICANT_DETAILS:
      return {
        ...state,
        applicantDetails: [
          ...(state.applicantDetails || []).filter(
            (existingCandidate) =>
              !action.payload.some(
                (newCandidate) => newCandidate.id === existingCandidate.id,
              ),
          ),
          ...action.payload,
        ],
      };
    case GET_CANDIDATE_LIST:
      return {
        ...state,
        candidateList: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"microsoft-login-success"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case MICROSOFT_LOGIN_SUCCESS:
      return {
        ...state,
        // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
        microsoftLoginResponse: action.payload,
      };
    // @ts-expect-error - TS2678 - Type '"update-internal-form-config"' is not comparable to type '"update-job-information" | "get-job-grade" | "get-job-location" | "get-base-info" | "get-pending-approvals" | "get-job-department" | "get-custom-job-fields" | "update-company-jd-generate" | ... 42 more ... | "edit-job-position"'.
    case UPDATE_INTERNAL_FORM_CONFIG:
      // @ts-expect-error - TS2339 - Property 'payload' does not exist on type 'never'.
      const updatedStepCandidate = action.payload || {};
      const jobId = updatedStepCandidate.jobId;
      const stepCandidateId = updatedStepCandidate.stepId;
      const newJobInternalForm = updatedStepCandidate.jobInternalForm || {};
      const updatedApplications = state.applicantInformation?.applications?.map(
        (app: CandidateApplication) => {
          if (app.jobId === jobId) {
            let stepExists = false;
            const updatedStepCandidates = app.stepCandidates.map((sc) => {
              if (sc.stepId === stepCandidateId) {
                stepExists = true;
                return {
                  ...sc,
                  jobInternalForm: newJobInternalForm,
                };
              }
              return sc;
            });

            if (!stepExists) {
              updatedStepCandidates.push(updatedStepCandidate);
            }

            return {
              ...app,
              stepCandidates: updatedStepCandidates,
            };
          }
          return app;
        },
      );

      return {
        ...state,
        applicantInformation: {
          ...state.applicantInformation,
          applications: updatedApplications || [],
        },
      };
    case FETCH_PROFILE_ANALYSIS: {
      const {candidateId} = action.payload;
      const key = String(candidateId);
      return {
        ...state,
        profileAnalysis: {
          ...state.profileAnalysis,
          loadingByCandidateId: {
            ...state.profileAnalysis?.loadingByCandidateId,
            [key]: true,
          },
          errorByCandidateId: {
            ...state.profileAnalysis?.errorByCandidateId,
            [key]: null,
          },
        },
      };
    }
    case FETCH_PROFILE_ANALYSIS_SUCCESS: {
      const {candidateId, data} = action.payload;
      const key = String(candidateId);
      return {
        ...state,
        profileAnalysis: {
          ...state.profileAnalysis,
          dataByCandidateId: {
            ...state.profileAnalysis?.dataByCandidateId,
            [key]: data,
          },
          loadingByCandidateId: {
            ...state.profileAnalysis?.loadingByCandidateId,
            [key]: false,
          },
        },
      };
    }
    case FETCH_PROFILE_ANALYSIS_ERROR: {
      const {candidateId, error} = action.payload;
      const key = String(candidateId);
      return {
        ...state,
        profileAnalysis: {
          ...state.profileAnalysis,
          loadingByCandidateId: {
            ...state.profileAnalysis?.loadingByCandidateId,
            [key]: false,
          },
          errorByCandidateId: {
            ...state.profileAnalysis?.errorByCandidateId,
            [key]: error,
          },
        },
      };
    }
    case SET_PROFILE_ANALYSIS_FEEDBACK: {
      const {candidateId, feedback} = action.payload;
      const key = String(candidateId);
      return {
        ...state,
        profileAnalysis: {
          ...state.profileAnalysis,
          feedbackByCandidateId: {
            ...state.profileAnalysis?.feedbackByCandidateId,
            [key]: feedback,
          },
        },
      };
    }
    case SET_PROFILE_ANALYSIS_FEEDBACK_LOADING: {
      const {candidateId, loading} = action.payload;
      const key = String(candidateId);
      return {
        ...state,
        profileAnalysis: {
          ...state.profileAnalysis,
          feedbackLoadingByCandidateId: {
            ...state.profileAnalysis?.feedbackLoadingByCandidateId,
            [key]: loading,
          },
        },
      };
    }
    case GET_UNTRIED_COUNT:
      return {
        ...state,
        untriedCount: action.payload,
      };
  }

  return state;
};

export default reducer;
